import { useTranslation } from "react-i18next";

import { FiscalDeclarationPrompt } from "@/components/features/notarial-requests/fiscal-declaration-prompt";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export interface FiscalDeclarationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a save that actually unblocks sending — see the prompt. */
  onSaved: () => void;
  submitLabel?: string;
}

/**
 * The declaration, as a dialog.
 *
 * WHY A DIALOG AND NOT THE INLINE PANEL IT REPLACED: inline, the form sat in
 * the page's flow competing with the request's own content, and a professional
 * interrupted mid-quote had to find it. A dialog puts the one thing they must
 * do in front of everything else and dismisses cleanly when it is done.
 *
 * THE SEND BUTTON MUST NOT COME BACK WHILE THIS IS OWED. The inline version
 * got that right by REPLACING the quote card — "leaving an active Send button
 * next to it would invite them to press a thing that refuses them again". A
 * dialog alone would undo that, because closing it reveals the live form
 * underneath. So the caller keeps replacing the card with a short notice, and
 * this dialog is what the notice opens: dismissing it returns to the notice,
 * never to a button that will refuse.
 *
 * `variant="plain"` because the dialog is already the container; the prompt's
 * own bordered panel inside it reads as a form that lost its frame.
 */
export function FiscalDeclarationDialog({
  open,
  onOpenChange,
  onSaved,
  submitLabel,
}: FiscalDeclarationDialogProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("auth.professionalFiscal.sectionTitle")}</DialogTitle>
          <DialogDescription>
            {t("auth.professionalFiscal.promptReason")}
          </DialogDescription>
        </DialogHeader>
        <FiscalDeclarationPrompt
          variant="plain"
          submitLabel={submitLabel}
          onSaved={() => {
            onOpenChange(false);
            onSaved();
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
